import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Estimate } from './estimates.model';
import { CustomerAddress, CustomerObject } from 'containers/customers/customers.model';
import { RootState } from 'store/reducers';
import { CreateEstimateErrorTypes } from './constants';
import { Job, JobAttachmentType, JobLeadSourceType } from 'containers/jobs/jobs.model';
import {
  InvoiceLineItem,
  InvoiceSurchargeState,
  TaxesAndFeesPayloadState,
  calculateFinalAmount,
  calculateSummatedLineItemLevelTaxesCurrency,
  calculateSurchargeInCurrency,
  calculateSurchargeInCurrencyWithBps,
  calculateSurchargeWithBpsPercentage,
  getLineItemsTotal,
  hasServiceCharge,
  updateServiceChargeWithBaseAmount
} from 'containers/invoices/InvoicesSlice';
import { DiscountFormData, LineItemAdditionalTax } from 'containers/invoices/types';
import {
  DiscountSettings,
  IInvoiceServiceCharge,
  IInvoiceServiceChargeFromResponse,
  InvoiceByIdResponse,
  InvoiceStatus
} from 'containers/invoices/invoices.model';
import { InvoiceDraftModalData } from 'containers/invoices/create-invoice/types';
import { AbstractITax, ITax } from 'containers/product-catalog/taxes/taxes.model';
import { GlobalServiceChargeTax } from 'containers/product-catalog/catalog.model';
import {
  calculateInvoiceSubtotalAndNewDiscount,
  calculateTotalOriginalUnitCost,
  isSameCatalogLineItem
} from 'containers/invoices/utils';
import { initServiceCharge } from 'containers/invoices/create-invoice/constants';
import { findAppliedUniqueTaxes } from 'containers/invoices/edit-invoice/invoicesEditSlice';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { FeatureToggleState } from 'containers/settings/constants';
import {
  parseLineItemTaxesFromResponse,
  prepareInvoiceAttachments,
  prepareInvoiceServiceCharge
} from 'containers/invoices/api';
import { DISCOUNT_TYPE } from 'constants/common';
import { LeadSourceOptions } from 'containers/jobs/constant';

// Helper function to get default expiry date (60 days from now) as ISO string
const getDefaultExpiryDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 60);
  date.setHours(23, 59, 59);
  return date.toISOString();
};

interface EstimatesState {
  estimates: Estimate[];
  currentEstimate: Estimate | null;
  estimateCustomer: CustomerObject | null;
  initialEstimateCustomer: CustomerObject | null;
  serviceAddress: CustomerAddress | null;
  initialServiceAddress: CustomerAddress | null;
  billingAddress: CustomerAddress | null;
  initialBillingAddress: CustomerAddress | null;
  estimateTitle: string;
  estimateDescription: string;
  loading: boolean;
  error: string | null;
  validationErrors: { type: CreateEstimateErrorTypes; errorMsg: string }[];
  expiryDate: string;
  notes: string;
  attachments: JobAttachmentType[];
  isSignatureEnabled: boolean;
  estimateLineItems: InvoiceLineItem[];
  initialEstimateLineItems: InvoiceLineItem[];
  estimateLineItemsTotal: number;
  estimateSubTotal: number;
  initialEstimateSubTotal: number;
  estimateFinalAmount: number;
  estimateDiscountFormData: DiscountFormData;
  estimateSelectedServiceCharge: IInvoiceServiceCharge;
  initialEstimateSelectedServiceCharge: IInvoiceServiceCharge;
  estimateCurrentSurcharge: InvoiceSurchargeState;
  initialEstimateCurrentSurcharge: InvoiceSurchargeState;
  estimateAppliedLineItemLevelTaxes: LineItemAdditionalTax[];
  estimateIsServiceFeeEnabled: boolean;
  initialEstimateIsServiceFeeEnabled: boolean;
  estimateGlobalLineItemTaxes: ITax[];
  estimateGlobalServiceChargeTaxes: GlobalServiceChargeTax[];
  estimateIsBackFromCreateTaxRoute: boolean;
  estimateDraftModalData?: InvoiceDraftModalData;
  discountSettings: DiscountSettings;
  isInitialisingEstimateFromAPI: boolean;
  assignees: AssignableMember[];
  entityId?: string;
  entityNumber?: string;
  isEstimateFromOtherEntities: boolean;
  entityType?: 'JOB' | 'INVOICE';
  estimateReferenceNumber: string;
  initialEstimateReferenceNumber: string;
  leadSource: string;
  leadSourceType: JobLeadSourceType;
  estimatesTnc: string;
  estimateTncEdited: boolean;
  invoiceNumber: string;
  jobNumber: string;
  relatedInvoiceStatus: InvoiceStatus;
  isFromInvoice: boolean;
  isFromJob: boolean;
  estimateCreatedAt: string;
  estimateUpdatedAt: string;
  estimateNumber: string;
  jobId: string;
  invoiceId: string;
  totalOriginalUnitCost: number;
}

const initialState: EstimatesState = {
  estimates: [],
  currentEstimate: null,
  estimateCustomer: null,
  initialEstimateCustomer: null,
  serviceAddress: null,
  initialServiceAddress: null,
  billingAddress: null,
  initialBillingAddress: null,
  estimateTitle: '',
  estimateDescription: '',
  loading: false,
  error: null,
  validationErrors: [],
  expiryDate: getDefaultExpiryDate(),
  notes: '',
  attachments: [],
  isSignatureEnabled: false,
  estimateLineItems: [],
  initialEstimateLineItems: [],
  estimateLineItemsTotal: 0,
  estimateSubTotal: 0,
  initialEstimateSubTotal: 0,
  estimateFinalAmount: 0,
  estimateDiscountFormData: {
    discountAmount: '0',
    discountPercent: '0',
    isDiscountAmountSelected: true,
    discountInCurrency: '0',
    amountAfterDiscount: '0'
  },
  estimateSelectedServiceCharge: initServiceCharge,
  initialEstimateSelectedServiceCharge: initServiceCharge,
  estimateCurrentSurcharge: {
    enabled: false,
    basePoints: null,
    percentage: null,
    percentageWithBps: null,
    valueInCurrency: null,
    valueInCurrencyWithBps: null,
    config: null
  },
  initialEstimateCurrentSurcharge: {
    enabled: false,
    basePoints: null,
    percentage: null,
    percentageWithBps: null,
    valueInCurrency: null,
    valueInCurrencyWithBps: null,
    config: null
  },
  estimateAppliedLineItemLevelTaxes: [],
  estimateIsServiceFeeEnabled: false,
  initialEstimateIsServiceFeeEnabled: false,
  estimateGlobalLineItemTaxes: [],
  estimateGlobalServiceChargeTaxes: [],
  estimateIsBackFromCreateTaxRoute: false,
  estimateDraftModalData: null,
  discountSettings: { enabled: true },
  isInitialisingEstimateFromAPI: false,
  assignees: [],
  entityId: '',
  entityNumber: '',
  isEstimateFromOtherEntities: false, // Default value
  entityType: null, // Default value
  estimateReferenceNumber: '', // Default value for reference number
  initialEstimateReferenceNumber: '', // Default value for initial reference number
  leadSource: '', // Default value for lead source
  leadSourceType: null, // Default value for lead source type
  estimatesTnc: '',
  estimateTncEdited: false,
  invoiceNumber: '',
  jobNumber: '',
  relatedInvoiceStatus: null,
  isFromInvoice: false,
  isFromJob: false,
  estimateCreatedAt: '',
  estimateUpdatedAt: '',
  estimateNumber: '',
  jobId: '',
  invoiceId: '',
  totalOriginalUnitCost: 0
};

const estimatesSlice = createSlice({
  name: 'estimates',
  initialState,
  reducers: {
    setEstimates: (state, action: PayloadAction<Estimate[]>) => {
      state.estimates = action.payload;
    },
    setCurrentEstimate: (state, action: PayloadAction<Estimate>) => {
      state.currentEstimate = action.payload;
    },
    updateEstimateCustomer: (state, action: PayloadAction<CustomerObject>) => {
      state.estimateCustomer = action.payload;
    },
    updateServiceAddress: (state, action: PayloadAction<CustomerAddress>) => {
      state.serviceAddress = action.payload;
    },
    updateBillingAddress: (state, action: PayloadAction<CustomerAddress>) => {
      state.billingAddress = action.payload;
    },
    updateEstimateTitle: (state, action: PayloadAction<string>) => {
      state.estimateTitle = action.payload;
    },
    updateEstimateDescription: (state, action: PayloadAction<string>) => {
      state.estimateDescription = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addEstimateValidationError: (
      state,
      action: PayloadAction<{ type: CreateEstimateErrorTypes; errorMsg: string }>
    ) => {
      state.validationErrors.push(action.payload);
    },
    removeEstimateValidationErrors: (state, action: PayloadAction<CreateEstimateErrorTypes>) => {
      state.validationErrors = state.validationErrors.filter(error => error.type !== action.payload);
    },
    resetEstimateState: state => {
      return { ...initialState, estimates: state.estimates };
    },
    updateExpiryDate: (state, action: PayloadAction<string | null>) => {
      state.expiryDate = action.payload || getDefaultExpiryDate();
    },
    updateEstimateNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
    },
    updateIsSignatureEnabled: (state, action: PayloadAction<boolean>) => {
      state.isSignatureEnabled = action.payload;
    },
    addEstimateLineItem: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      const updatedLineItemsList = [
        ...state.estimateLineItems,
        {
          ...payload,
          id: state.estimateLineItems.length.toString(),
          total: +payload.unitCost * +payload.quantity,
          originalUnitCost: payload.originalUnitCost,
          markupPercentage: payload.markupPercentage
        }
      ];
      state.estimateLineItems = updatedLineItemsList;

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.estimateLineItemsTotal = totalCostOfLineItems;
      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.estimateDiscountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.estimateDiscountFormData };

      state.estimateSubTotal = subtotal;
      state.estimateDiscountFormData = newDiscountData;

      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );
        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrency: surchargeInCurrency,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
      }

      // Update applied line item taxes array
      const itemLevelTaxesInPayload = payload?.taxes ?? [];
      let modifiedAppliedLineItemLevelTaxes: LineItemAdditionalTax[] = state.estimateAppliedLineItemLevelTaxes;
      if (itemLevelTaxesInPayload.length > 0) {
        itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
          const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
            tax => tax.id === itemLevelTaxInPayload.id
          );

          if (!isLineItemTaxAlreadySelected) {
            modifiedAppliedLineItemLevelTaxes = [
              ...modifiedAppliedLineItemLevelTaxes,
              itemLevelTaxInPayload as LineItemAdditionalTax
            ];
          }
        });
      }
      state.estimateAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;

      // Calculate service charge base amount
      let updatedServiceCharge = state.estimateSelectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.estimateCurrentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);
    },
    modifyEstimateLineItem: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      let lineItemToUpdate: InvoiceLineItem = null;
      const updatedLineItemsList = state.estimateLineItems.map(lineItem => {
        if (lineItem.id === payload.id) {
          lineItemToUpdate = lineItem;
          return {
            ...lineItem,
            itemDescription: payload.itemDescription,
            unitCost: payload.unitCost,
            originalUnitCost: payload.originalUnitCost,
            markupPercentage: payload.markupPercentage,
            quantity: payload.quantity,
            total: +payload.unitCost * +payload.quantity,
            additionalDetails: payload.additionalDetails,
            ...(payload?.taxes ? { taxes: payload.taxes } : { taxes: [] })
          };
        }
        return lineItem;
      });

      state.estimateLineItems = updatedLineItemsList;

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.estimateLineItemsTotal = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.estimateDiscountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.estimateDiscountFormData };

      state.estimateSubTotal = subtotal;
      state.estimateDiscountFormData = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);
        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrency: surchargeInCurrency,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
      }

      // Calculate service charge base amount
      let updatedServiceCharge = state.estimateSelectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      // Update applied line item taxes array
      const itemLevelTaxesInPayload = payload?.taxes ?? [];
      const taxesInItemBeforeUpdate = lineItemToUpdate?.taxes ?? [];

      if (itemLevelTaxesInPayload.length > 0) {
        itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
          const isLineItemTaxAlreadySelected = state.estimateAppliedLineItemLevelTaxes.find(
            tax => tax.id === itemLevelTaxInPayload.id
          );
          if (!isLineItemTaxAlreadySelected) {
            state.estimateAppliedLineItemLevelTaxes = [
              ...state.estimateAppliedLineItemLevelTaxes,
              itemLevelTaxInPayload as LineItemAdditionalTax
            ];
          }
        });
      }

      if (taxesInItemBeforeUpdate.length > 0) {
        taxesInItemBeforeUpdate.forEach(taxInItemBeforeUpdate => {
          const isTaxAppliedOnOtherItem = updatedLineItemsList.find(lineItem => {
            return lineItem?.taxes?.find(lineItemTax => lineItemTax.id === taxInItemBeforeUpdate.id);
          });
          const isTaxAppliedOnServiceCharge = state.estimateSelectedServiceCharge?.taxes?.find(
            tax => tax.id === taxInItemBeforeUpdate.id
          );
          if (!isTaxAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
            state.estimateAppliedLineItemLevelTaxes = state.estimateAppliedLineItemLevelTaxes.filter(
              tax => tax.id !== taxInItemBeforeUpdate.id
            );
          }
        });
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.estimateCurrentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);
    },
    removeEstimateLineItem: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      const updatedLineItemsList = state.estimateLineItems.filter(lineItem => lineItem.id !== payload.id);

      if (updatedLineItemsList.length > 0) {
        state.estimateLineItems = updatedLineItemsList;
        const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
        state.estimateLineItemsTotal = totalCostOfLineItems;
        state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);
        const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
          totalCostOfItems: totalCostOfLineItems,
          discountData: state.estimateDiscountFormData
        }) ?? { subtotal: totalCostOfLineItems, discountData: state.estimateDiscountFormData };

        state.estimateSubTotal = subtotal;
        state.estimateDiscountFormData = newDiscountData;

        // Update surcharge state
        let surchargeInCurrencyWithBps = 0;
        if (state.estimateIsServiceFeeEnabled) {
          const surchargeInCurrency: number = calculateSurchargeInCurrency(
            state.estimateCurrentSurcharge.percentage,
            subtotal
          );
          surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
            surchargeInCurrency,
            state.estimateCurrentSurcharge.basePoints
          );

          const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

          state.estimateCurrentSurcharge = {
            ...state.estimateCurrentSurcharge,
            percentageWithBps: surchargeWithBpsPercentage,
            valueInCurrency: surchargeInCurrency,
            valueInCurrencyWithBps: surchargeInCurrencyWithBps
          };
        }

        // Calculate service charge base amount
        let updatedServiceCharge = state.estimateSelectedServiceCharge;
        let serviceChargeBaseAmount = 0;
        if (hasServiceCharge(updatedServiceCharge)) {
          const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
            updateServiceChargeWithBaseAmount({
              serviceCharge: updatedServiceCharge,
              subtotal: subtotal
            });
          serviceChargeBaseAmount = baseAmount;
          updatedServiceCharge = updatedServiceChargeWithBaseAmount;
          state.estimateSelectedServiceCharge = updatedServiceCharge;
        }

        // Update applied line item taxes array
        const singleItemLevelTaxesToRemove: LineItemAdditionalTax[] = payload?.taxes ?? [];
        if (singleItemLevelTaxesToRemove.length > 0) {
          singleItemLevelTaxesToRemove.forEach(itemLevelTaxOnCurrentItem => {
            const isTaxToRemoveAppliedOnOtherItem = updatedLineItemsList.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax?.id === itemLevelTaxOnCurrentItem?.id);
            });

            const isTaxAppliedOnServiceCharge = state.estimateSelectedServiceCharge?.taxes?.find(
              tax => tax.id === itemLevelTaxOnCurrentItem.id
            );

            if (!isTaxToRemoveAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
              state.estimateAppliedLineItemLevelTaxes = state.estimateAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== itemLevelTaxOnCurrentItem.id
              );
            }
          });
        }

        // Update final amount
        const surchargeInCurrencyBpsValue = state.estimateCurrentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

        const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
          lineItems: updatedLineItemsList,
          selectedServiceCharge: updatedServiceCharge
        });

        const finalAmount = calculateFinalAmount({
          subtotal,
          totalServiceChargeInCurrency: serviceChargeBaseAmount,
          surcharge: surchargeInCurrencyBpsValue,
          summatedLineItemLevelTaxesCurrency
        });
        state.estimateFinalAmount = finalAmount;
      } else {
        state.estimateLineItems = [];
        state.estimateLineItemsTotal = 0;
        state.estimateSubTotal = 0;
        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: null,
          valueInCurrency: null,
          valueInCurrencyWithBps: null
        };
        state.estimateSelectedServiceCharge = initServiceCharge;
        state.estimateAppliedLineItemLevelTaxes = [];
        state.estimateDiscountFormData = {
          discountAmount: '0',
          discountPercent: '0',
          isDiscountAmountSelected: true,
          discountInCurrency: '0',
          amountAfterDiscount: '0'
        };
        state.estimateFinalAmount = 0;
      }

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(state.estimateLineItems);
    },
    incrementEstimateLineItemQty: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      const updatedLineItemsList = state.estimateLineItems.map(lineItem => {
        if (isSameCatalogLineItem({ primaryItem: payload, secondaryItem: lineItem })) {
          const newQty = (+lineItem.quantity + 1).toString();
          const totalLineItemCost = +newQty * +lineItem.unitCost;
          let lineItemAdditionalTaxesWithAmount: LineItemAdditionalTax[] = null;
          const lineItemTaxes: LineItemAdditionalTax[] = lineItem?.taxes ?? [];
          if (lineItemTaxes.length > 0) {
            lineItemAdditionalTaxesWithAmount = lineItemTaxes.map(tax => ({
              ...tax,
              amount: (Number(tax.percentage) / 100) * totalLineItemCost
            }));
          }

          return {
            ...lineItem,
            quantity: newQty,
            total: totalLineItemCost,
            ...(lineItemAdditionalTaxesWithAmount ? { taxes: lineItemAdditionalTaxesWithAmount } : {})
          };
        }
        return lineItem;
      });

      state.estimateLineItems = updatedLineItemsList;

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.estimateLineItemsTotal = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.estimateDiscountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.estimateDiscountFormData };

      state.estimateSubTotal = subtotal;
      state.estimateDiscountFormData = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);
        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrency: surchargeInCurrency,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
      }

      // Calculate service charge base amount
      let updatedServiceCharge = state.estimateSelectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.estimateCurrentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(state.estimateLineItems);
    },
    updateEstimateDiscountFormData: (state, { payload }: PayloadAction<DiscountFormData>) => {
      state.estimateDiscountFormData = payload;

      const subtotal = state.estimateLineItemsTotal - +payload.discountInCurrency;

      state.estimateSubTotal = subtotal;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );
        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrency: surchargeInCurrency,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
      }

      // Calculate service charge base amount
      let updatedServiceCharge = state.estimateSelectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.estimateCurrentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.estimateLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        surcharge: surchargeInCurrencyBpsValue,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(state.estimateLineItems);
    },
    removeEstimateDiscountFormData: state => {
      state.estimateDiscountFormData = {
        discountAmount: '0',
        discountPercent: '0',
        isDiscountAmountSelected: true,
        discountInCurrency: '0',
        amountAfterDiscount: '0'
      };

      const subtotal = state.estimateLineItemsTotal;

      state.estimateSubTotal = subtotal;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );
        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);
        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrency: surchargeInCurrency,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
      }

      // Calculate service charge base amount
      let updatedServiceCharge = state.estimateSelectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.estimateCurrentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.estimateLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(state.estimateLineItems);
    },
    updateSelectedEstimateServiceCharge: (state, { payload }: PayloadAction<IInvoiceServiceCharge>) => {
      const currentServiceCharge: IInvoiceServiceCharge = { ...state.estimateSelectedServiceCharge };
      const newServiceCharge = payload;
      state.estimateSelectedServiceCharge = newServiceCharge;

      // Update final amount

      // calculate service charge base amount
      let updatedServiceCharge = newServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: state.estimateSubTotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      const taxesInNewServiceCharge: AbstractITax[] = newServiceCharge?.taxes ?? [];
      const taxesInCurrentServiceCharge: AbstractITax[] = currentServiceCharge?.taxes ?? [];

      //  add taxes which are in the new service charge if they aren't already present in any of the line items
      if (
        (currentServiceCharge === initServiceCharge ||
          currentServiceCharge === null ||
          taxesInCurrentServiceCharge.length === 0) &&
        taxesInNewServiceCharge.length > 0
      ) {
        taxesInNewServiceCharge.forEach(serviceChargeTax => {
          const isLineItemTaxAlreadySelected = state.estimateAppliedLineItemLevelTaxes.find(
            tax => tax.id === serviceChargeTax.id
          );
          if (!isLineItemTaxAlreadySelected) {
            state.estimateAppliedLineItemLevelTaxes = [
              ...state.estimateAppliedLineItemLevelTaxes,
              serviceChargeTax as LineItemAdditionalTax
            ];
          }
        });
      } else {
        if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length > 0) {
          // Remove the taxes which were added because of the current service charge
          let modifiedAppliedLineItemLevelTaxes: LineItemAdditionalTax[] = [...state.estimateAppliedLineItemLevelTaxes];
          taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
            const isTaxAppliedOnAnyLineItem = state.estimateLineItems.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
            });
            if (!isTaxAppliedOnAnyLineItem) {
              modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== serviceChargeTax.id
              );
            }
          });

          //  add the taxes which are in the new service charge
          taxesInNewServiceCharge.forEach(serviceChargeTax => {
            const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
              tax => tax.id === serviceChargeTax.id
            );
            if (!isLineItemTaxAlreadySelected) {
              modifiedAppliedLineItemLevelTaxes = [
                ...modifiedAppliedLineItemLevelTaxes,
                serviceChargeTax as LineItemAdditionalTax
              ];
            }
          });
          state.estimateAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
        } else if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length === 0) {
          // Remove the taxes which were added because of the current service charge
          let modifiedAppliedLineItemLevelTaxes: LineItemAdditionalTax[] = [...state.estimateAppliedLineItemLevelTaxes];
          taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
            const isTaxAppliedOnAnyLineItem = state.estimateLineItems.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
            });
            if (!isTaxAppliedOnAnyLineItem) {
              modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== serviceChargeTax.id
              );
            }
          });
          state.estimateAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
        }
      }

      const surchargeInCurrencyBpsValue = state.estimateIsServiceFeeEnabled
        ? state.estimateCurrentSurcharge.valueInCurrencyWithBps
        : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.estimateLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.estimateSubTotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(state.estimateLineItems);
    },

    removeEstimateServiceCharge: state => {
      const currentSelectedServiceCharge = { ...state.estimateSelectedServiceCharge };
      state.estimateSelectedServiceCharge = initServiceCharge;

      // Update final amount
      const surchargeInCurrencyBpsValue = state.estimateIsServiceFeeEnabled
        ? state.estimateCurrentSurcharge.valueInCurrencyWithBps
        : 0;

      // Update applied line item taxes array
      const serviceChargeTaxesToRemove: LineItemAdditionalTax[] = currentSelectedServiceCharge?.taxes ?? [];
      let modifiedAppliedLineItemLevelTaxes: LineItemAdditionalTax[] = [...state.estimateAppliedLineItemLevelTaxes];
      if (serviceChargeTaxesToRemove.length > 0) {
        serviceChargeTaxesToRemove.forEach(taxInCurrentServiceCharge => {
          const isTaxToRemoveAppliedOnAnyLineItem: InvoiceLineItem = state.estimateLineItems.find(lineItem => {
            return lineItem?.taxes?.find(tax => tax?.id === taxInCurrentServiceCharge?.id);
          });
          if (!isTaxToRemoveAppliedOnAnyLineItem) {
            modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
              tax => tax.id !== taxInCurrentServiceCharge.id
            );
          }
        });
        state.estimateAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
      }

      // Update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.estimateLineItems
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.estimateSubTotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: 0,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;
    },

    updateIsServiceFeeEnabledInEstimates: (state, { payload }: PayloadAction<boolean>) => {
      state.estimateIsServiceFeeEnabled = payload;
      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          state.estimateSubTotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(
          surchargeInCurrencyWithBps,
          state.estimateSubTotal
        );

        state.estimateCurrentSurcharge = {
          ...state.estimateCurrentSurcharge,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrency: surchargeInCurrency,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
      }
      // Update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.estimateLineItems
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.estimateSubTotal,
        surcharge: surchargeInCurrencyWithBps,
        totalServiceChargeInCurrency: +state.estimateSelectedServiceCharge.totalAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;
    },

    updateLineItemsOrderInEstimatesSlice: (state, { payload }: PayloadAction<InvoiceLineItem[]>) => {
      state.estimateLineItems = payload;
    },

    updateEstimateDraftData: (state, { payload }: PayloadAction<InvoiceDraftModalData>) => {
      state.estimateDraftModalData = payload;
    },

    resetEstimateDraftData: state => {
      state.estimateDraftModalData = null;
    },

    updateIsBackFromCreateTaxInEstimatesSlice: (state, { payload }: PayloadAction<boolean>) => {
      state.estimateIsBackFromCreateTaxRoute = payload;
    },

    initializeEstimateForEdit: (state, action: PayloadAction<Estimate>) => {
      const estimateData = action.payload;
      state.estimateNumber = estimateData.estimateNumber;
      state.isFromInvoice = estimateData.invoices?.length > 0;
      state.isFromJob = estimateData.jobs?.length > 0;
      state.jobId = estimateData.jobs?.at(0)?.jobId ?? '';
      state.invoiceId = estimateData.invoices?.at(0)?.id ?? '';
      state.jobNumber = estimateData.jobs?.at(0)?.jobNumber ?? '';
      state.relatedInvoiceStatus = (estimateData.invoices?.at(0)?.status as InvoiceStatus) || null;
      state.invoiceNumber = estimateData.invoices?.at(0)?.invoiceNumber ?? '';
      state.estimateCustomer = {
        ...estimateData.customer,
        ID: estimateData.customer.id,
        isBillingSameAsService: estimateData.customer.isBillingSameAsService,
        merchantId: '',
        billingAddress: estimateData.customer?.billingAddress ?? null
      };
      state.initialEstimateCustomer = state.estimateCustomer;
      state.serviceAddress = estimateData.customer?.serviceAddress?.[0] ?? null;
      state.initialServiceAddress = state.serviceAddress;
      state.billingAddress = estimateData.customer?.billingAddress ?? null;
      state.initialBillingAddress = state.billingAddress;
      state.estimateTitle = estimateData.title;
      state.estimateDescription = estimateData.description;
      state.expiryDate = estimateData.expiresAt;
      state.notes = estimateData.note || '';
      state.attachments = prepareInvoiceAttachments(estimateData.attachments);
      const parsedLineItems =
        estimateData.lineItems?.map(
          lineItem =>
            ({
              ...lineItem,
              itemDescription: lineItem.name,
              unitCost: lineItem.unitPrice,
              originalUnitCost: lineItem.unitCost ?? '',
              markupPercentage: lineItem.markupPercentage ?? '',
              quantity: lineItem.qty.toString(),
              total: +lineItem.unitPrice * +lineItem.qty,
              id: lineItem.id.toString(),
              taxes: parseLineItemTaxesFromResponse(lineItem.taxes)
            } as InvoiceLineItem)
        ) ?? [];
      state.estimateLineItems = parsedLineItems;
      state.initialEstimateLineItems = parsedLineItems;
      const parsedServiceCharges = prepareInvoiceServiceCharge({
        serviceCharge: estimateData.serviceCharges?.at(0) as unknown as IInvoiceServiceChargeFromResponse
      });

      state.estimateAppliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: parsedLineItems,
        serviceCharge: parsedServiceCharges
      });

      // Calculate line items total
      state.estimateLineItemsTotal = getLineItemsTotal(state.estimateLineItems);

      const initialDiscountFormData: DiscountFormData = {
        discountAmount: estimateData.discount,
        discountPercent: estimateData.discount,
        isDiscountAmountSelected: estimateData.discountType === DISCOUNT_TYPE.FLAT,
        discountInCurrency: estimateData.discount,
        amountAfterDiscount: null
      };
      // Calculate subtotal and update discount form data
      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: state.estimateLineItemsTotal,
        discountData: initialDiscountFormData
      });

      state.estimateSubTotal = subtotal;
      state.initialEstimateSubTotal = subtotal;
      if (state.discountSettings.enabled) {
        state.estimateDiscountFormData = newDiscountData;
      }

      if (!state.estimateCurrentSurcharge.enabled) {
        state.estimateIsServiceFeeEnabled = false;
      } else if (state.estimateCurrentSurcharge.config === FeatureToggleState.ALWAYS_ON) {
        state.estimateIsServiceFeeEnabled = true;
      } else state.estimateIsServiceFeeEnabled = estimateData.isServiceFeeEnabled;

      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.estimateCurrentSurcharge = {
          percentage: state.estimateCurrentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.estimateCurrentSurcharge.enabled,
          basePoints: state.estimateCurrentSurcharge.basePoints,
          config: state.estimateCurrentSurcharge.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
        state.initialEstimateCurrentSurcharge = state.estimateCurrentSurcharge;
      }

      const serviceChargeInPayload = parsedServiceCharges;
      let updatedServiceCharge = serviceChargeInPayload;
      let serviceChargeBaseAmount = 0;
      // calculate total service charge base amount
      if (hasServiceCharge(serviceChargeInPayload)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: serviceChargeInPayload,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
        state.initialEstimateSelectedServiceCharge = updatedServiceCharge;
      }

      const surchargeInCurrencyBpsValue = state.estimateIsServiceFeeEnabled ? surchargeInCurrencyWithBps : 0;
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: parsedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      state.estimateFinalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });

      state.assignees =
        estimateData.assignees?.map(({ id, ...rest }) => ({
          merchantId: id,
          ...rest,
          email: '',
          phone: '',
          id,
          profilePictureUrl: rest.profilePictureURL || ''
        })) ?? [];

      state.estimateReferenceNumber = estimateData.referenceNo;
      state.initialEstimateReferenceNumber = estimateData.referenceNo;
      state.leadSource = estimateData.leadSource;
      state.leadSourceType = LeadSourceOptions.find(leadSource => leadSource.id === estimateData.leadSourceType);
      state.validationErrors = [];
      state.loading = false;
      state.error = null;
      state.isInitialisingEstimateFromAPI = false;
      state.estimatesTnc = estimateData.tncEdited ? estimateData.tnc || '' : '';
      state.estimateTncEdited = estimateData.tncEdited;
      state.estimateCreatedAt = estimateData.createdAt;
      state.estimateUpdatedAt = estimateData.updatedAt;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);
    },

    updateIsInitialisingEstimateFromAPI: (state, action: PayloadAction<boolean>) => {
      state.isInitialisingEstimateFromAPI = action.payload;
    },

    updateAssignees: (state, action: PayloadAction<AssignableMember[]>) => {
      state.assignees = action.payload; // Update assignees
    },

    initializeEstimateFromJob: (state, action: PayloadAction<Job>) => {
      const jobData = action.payload;
      state.isFromJob = true;
      state.jobNumber = jobData?.jobNumber ?? '';
      state.jobId = jobData?.id ?? '';
      state.invoiceId = jobData.invoices?.at(0)?.id ?? '';
      state.relatedInvoiceStatus = (jobData.invoices?.at(0)?.status as InvoiceStatus) || null;
      state.invoiceNumber = jobData.invoices?.at(0)?.invoiceNumber ?? '';
      state.estimateCustomer = {
        ...jobData.customerLatest,
        ID: jobData.customerLatest.id,
        isBillingSameAsService: jobData.customerLatest.isBillingSameAsService,
        merchantId: '',
        billingAddress: jobData.customerLatest.billingAddress as CustomerAddress,
        serviceAddress: jobData.customerLatest.serviceAddress as CustomerAddress[]
      };
      state.initialEstimateCustomer = state.estimateCustomer;
      state.serviceAddress = jobData.customerLatest.serviceAddress[0] as CustomerAddress;
      state.initialServiceAddress = state.serviceAddress;
      state.billingAddress = jobData.customerLatest.billingAddress as CustomerAddress;
      state.initialBillingAddress = state.billingAddress;
      state.estimateTitle = jobData.title;
      state.estimateDescription = jobData.description;
      state.assignees =
        jobData.assignees?.map(({ id, ...rest }) => ({
          merchantId: id,
          ...rest,
          email: '',
          phone: '',
          id,
          profilePictureUrl: rest.profilePictureURL || '',
          colorCode: ''
        })) ?? [];

      const parsedLineItems =
        jobData.lineItems?.map(
          lineItem =>
            ({
              ...lineItem,
              itemDescription: lineItem.name,
              unitCost: lineItem.unitPrice,
              originalUnitCost: lineItem.unitCost,
              markupPercentage: lineItem.markupPercentage,
              quantity: lineItem.qty.toString(),
              total: +lineItem.unitPrice * +lineItem.qty,
              id: lineItem.id.toString(),
              taxes: parseLineItemTaxesFromResponse(lineItem.taxes)
            } as InvoiceLineItem)
        ) ?? [];
      state.estimateLineItems = parsedLineItems;
      state.initialEstimateLineItems = parsedLineItems;
      const parsedServiceCharges = prepareInvoiceServiceCharge({
        serviceCharge: jobData.serviceCharges?.at(0) as IInvoiceServiceChargeFromResponse
      });

      state.estimateAppliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: parsedLineItems,
        serviceCharge: parsedServiceCharges
      });

      // Calculate line items total
      state.estimateLineItemsTotal = getLineItemsTotal(state.estimateLineItems);

      const initialDiscountFormData: DiscountFormData = {
        discountAmount: jobData.discount,
        discountPercent: jobData.discount,
        isDiscountAmountSelected: jobData.discountType === DISCOUNT_TYPE.FLAT,
        discountInCurrency: jobData.discount,
        amountAfterDiscount: null
      };
      // Calculate subtotal and update discount form data
      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: state.estimateLineItemsTotal,
        discountData: initialDiscountFormData
      });

      state.estimateSubTotal = subtotal;
      state.initialEstimateSubTotal = subtotal;
      if (state.discountSettings.enabled) {
        state.estimateDiscountFormData = newDiscountData;
      }

      if (!state.estimateCurrentSurcharge.enabled) {
        state.estimateIsServiceFeeEnabled = false;
      } else if (state.estimateCurrentSurcharge.config === FeatureToggleState.ALWAYS_ON) {
        state.estimateIsServiceFeeEnabled = true;
      } else state.estimateIsServiceFeeEnabled = jobData.isServiceFeeEnabled;

      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.estimateCurrentSurcharge = {
          percentage: state.estimateCurrentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.estimateCurrentSurcharge.enabled,
          basePoints: state.estimateCurrentSurcharge.basePoints,
          config: state.estimateCurrentSurcharge.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
        state.initialEstimateCurrentSurcharge = state.estimateCurrentSurcharge;
      }

      const serviceChargeInPayload = parsedServiceCharges;
      let updatedServiceCharge = serviceChargeInPayload;
      let serviceChargeBaseAmount = 0;
      // calculate total service charge base amount
      if (hasServiceCharge(serviceChargeInPayload)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: serviceChargeInPayload,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
        state.initialEstimateSelectedServiceCharge = updatedServiceCharge;
      }
      const surchargeInCurrencyBpsValue = state.estimateIsServiceFeeEnabled ? surchargeInCurrencyWithBps : 0;
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: parsedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      state.estimateFinalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });

      // Initialize entityId and entityNumber
      state.entityId = jobData.id; // Assuming jobData has an id property
      state.entityNumber = jobData.jobNumber; // Assuming jobData has a jobNumber property

      // Initialize other fields as needed
      state.validationErrors = [];
      state.loading = false;
      state.error = null;
      state.isInitialisingEstimateFromAPI = false;
      state.isEstimateFromOtherEntities = true;
      state.entityType = 'JOB';

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);
    },

    initializeEstimateFromInvoice: (state, action: PayloadAction<InvoiceByIdResponse>) => {
      const invoiceData = action.payload;
      state.isFromInvoice = true;
      state.invoiceId = invoiceData.invoiceId;
      state.jobId = invoiceData?.jobData?.[0]?.jobId ?? '';
      state.invoiceNumber = invoiceData.invoiceNumber;
      state.relatedInvoiceStatus = invoiceData.invoiceStatus as InvoiceStatus;
      state.jobNumber = invoiceData?.jobData?.[0]?.jobNumber ?? '';
      // Initialize customer details
      state.estimateCustomer = {
        ...invoiceData.customer,
        ID: invoiceData.customer.id,
        merchantId: '',
        isBillingSameAsService: false,
        billingAddress: invoiceData.customer.billingAddress as CustomerAddress,
        serviceAddress: invoiceData.customer.serviceAddress as CustomerAddress[],
        companyName: invoiceData.customer.companyName || ''
      };
      state.initialEstimateCustomer = state.estimateCustomer;
      state.serviceAddress = invoiceData.customer.serviceAddress[0] as CustomerAddress;
      state.initialServiceAddress = state.serviceAddress;
      state.billingAddress = invoiceData.customer.billingAddress as CustomerAddress;
      state.initialBillingAddress = state.billingAddress;

      const parsedLineItems =
        invoiceData.lineItems?.map(
          (lineItem, index) =>
            ({
              ...lineItem,
              itemDescription: lineItem.name,
              unitCost: lineItem.unitPrice,
              originalUnitCost: lineItem.unitCost ?? '',
              markupPercentage: lineItem.markupPercentage ?? '',
              quantity: lineItem.qty.toString(),
              total: +lineItem.unitPrice * +lineItem.qty,
              id: index.toString(),
              taxes: parseLineItemTaxesFromResponse(lineItem.taxes)
            } as InvoiceLineItem)
        ) ?? [];
      state.estimateLineItems = parsedLineItems;
      state.initialEstimateLineItems = parsedLineItems;
      const parsedServiceCharges = prepareInvoiceServiceCharge({
        serviceCharge: invoiceData.serviceCharges?.at(0) as IInvoiceServiceChargeFromResponse
      });

      state.estimateAppliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: parsedLineItems,
        serviceCharge: parsedServiceCharges
      });

      // Calculate line items total
      state.estimateLineItemsTotal = getLineItemsTotal(state.estimateLineItems);

      const initialDiscountFormData: DiscountFormData = {
        discountAmount: invoiceData.discount,
        discountPercent: invoiceData.discount,
        isDiscountAmountSelected: invoiceData.isDiscountAmountSelected,
        discountInCurrency: invoiceData.discount,
        amountAfterDiscount: null
      };
      // Calculate subtotal and update discount form data
      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: state.estimateLineItemsTotal,
        discountData: initialDiscountFormData
      });

      state.estimateSubTotal = subtotal;
      state.initialEstimateSubTotal = subtotal;
      if (state.discountSettings.enabled) {
        state.estimateDiscountFormData = newDiscountData;
      }

      if (!state.estimateCurrentSurcharge.enabled) {
        state.estimateIsServiceFeeEnabled = false;
      } else if (state.estimateCurrentSurcharge.config === FeatureToggleState.ALWAYS_ON) {
        state.estimateIsServiceFeeEnabled = true;
      } else state.estimateIsServiceFeeEnabled = invoiceData.isServiceFeeEnabled;

      let surchargeInCurrencyWithBps = 0;
      if (state.estimateIsServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.estimateCurrentSurcharge.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.estimateCurrentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.estimateCurrentSurcharge = {
          percentage: state.estimateCurrentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.estimateCurrentSurcharge.enabled,
          basePoints: state.estimateCurrentSurcharge.basePoints,
          config: state.estimateCurrentSurcharge.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
        state.initialEstimateCurrentSurcharge = state.estimateCurrentSurcharge;
      }

      const serviceChargeInPayload = parsedServiceCharges;
      let updatedServiceCharge = serviceChargeInPayload;
      let serviceChargeBaseAmount = 0;
      // calculate total service charge base amount
      if (hasServiceCharge(serviceChargeInPayload)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: serviceChargeInPayload,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
        state.initialEstimateSelectedServiceCharge = updatedServiceCharge;
      }
      const surchargeInCurrencyBpsValue = state.estimateIsServiceFeeEnabled ? surchargeInCurrencyWithBps : 0;
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: parsedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      state.estimateFinalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });

      // Initialize entityId and entityNumber
      state.entityId = invoiceData.invoiceId; // Assuming invoiceData has an id property
      state.entityNumber = invoiceData.invoiceNumber; // Assuming invoiceData has an invoiceNumber property

      // Initialize other fields as needed
      state.validationErrors = [];
      state.loading = false;
      state.error = null;
      state.isEstimateFromOtherEntities = true;
      state.entityType = 'INVOICE';
      state.isInitialisingEstimateFromAPI = false;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);
    },

    setEstimateFromOtherEntities: (state, action: PayloadAction<boolean>) => {
      state.isEstimateFromOtherEntities = action.payload;
    },
    setEntityType: (state, action: PayloadAction<'JOB' | 'INVOICE'>) => {
      state.entityType = action.payload;
    },
    updateInvoiceFeeSettingsInEstimates: (state, { payload }: PayloadAction<TaxesAndFeesPayloadState>) => {
      if (payload.surchargeState.config === FeatureToggleState.OFF_BY_DEFAULT || !payload.surchargeState.enabled) {
        state.estimateIsServiceFeeEnabled = false;
      } else state.estimateIsServiceFeeEnabled = true;

      const surchargeInCurrency: number = calculateSurchargeInCurrency(
        payload.surchargeState.percentage,
        state.estimateSubTotal
      );

      const surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
        surchargeInCurrency,
        payload.surchargeState.basePoints
      );

      const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(
        surchargeInCurrencyWithBps,
        state.estimateSubTotal
      );

      state.estimateCurrentSurcharge = {
        percentageWithBps: surchargeWithBpsPercentage,
        valueInCurrencyWithBps: surchargeInCurrencyWithBps,
        percentage: payload.surchargeState.percentage,
        valueInCurrency: surchargeInCurrency,
        config: payload.surchargeState.config,
        enabled: payload.surchargeState.enabled,
        basePoints: payload.surchargeState.basePoints
      };

      state.discountSettings = payload.discountSettings;

      // calculate service charge base amount
      let updatedServiceCharge = state.estimateSelectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: state.estimateSubTotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.estimateSelectedServiceCharge = updatedServiceCharge;
      }

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.estimateLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.estimateSubTotal,
        surcharge: state.estimateIsServiceFeeEnabled ? surchargeInCurrencyWithBps : 0,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.estimateFinalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(state.estimateLineItems);
    },

    // Action to update estimate attachments
    updateEstimateAttachments: (state, { payload }: PayloadAction<JobAttachmentType>) => {
      const id = state.attachments.length + 1;
      const updatedAttachments = [
        ...state.attachments,
        { ...payload, id: payload.id ? payload.id.toString() : id.toString() }
      ];
      state.attachments = updatedAttachments;
    },

    // Action to remove estimate attachments
    removeEstimateAttachments: (state, { payload }: PayloadAction<JobAttachmentType>) => {
      const updatedAttachments = state.attachments.filter(attachment => attachment.url !== payload.url);
      state.attachments = updatedAttachments;
    },

    // Action to modify existing estimate attachments
    modifyEstimateAttachment: (state, { payload }: PayloadAction<JobAttachmentType>) => {
      const updatedAttachmentList = state.attachments.map(attachment => {
        if (attachment.id === payload.id) {
          return {
            ...attachment,
            url: payload.url,
            title: payload.title,
            imagePreview: payload.imagePreview
          };
        }
        return attachment;
      });
      state.attachments = updatedAttachmentList;
    },

    resetEstimateAttachmentState: state => {
      state.attachments = [];
    },

    updateEstimateReferenceNumber: (state, action: PayloadAction<string>) => {
      state.estimateReferenceNumber = action.payload;
    },

    updateLeadSource: (state, action: PayloadAction<string>) => {
      state.leadSource = action.payload;
    },

    updateLeadSourceType: (state, action: PayloadAction<JobLeadSourceType>) => {
      state.leadSourceType = action.payload;
    },

    updateEstimatesTnc: (state, action: PayloadAction<string>) => {
      state.estimatesTnc = action.payload;
    }
  }
});

export const {
  setEstimates,
  setCurrentEstimate,
  updateEstimateCustomer,
  updateServiceAddress,
  updateBillingAddress,
  updateEstimateTitle,
  updateEstimateDescription,
  setLoading,
  setError,
  addEstimateValidationError,
  removeEstimateValidationErrors,
  resetEstimateState,
  updateExpiryDate,
  updateEstimateNotes,
  modifyEstimateAttachment,
  removeEstimateAttachments,
  updateEstimateAttachments,
  updateIsSignatureEnabled,
  addEstimateLineItem,
  modifyEstimateLineItem,
  removeEstimateLineItem,
  incrementEstimateLineItemQty,
  updateEstimateDiscountFormData,
  removeEstimateDiscountFormData,
  updateSelectedEstimateServiceCharge,
  removeEstimateServiceCharge,
  updateIsServiceFeeEnabledInEstimates,
  updateLineItemsOrderInEstimatesSlice,
  updateEstimateDraftData,
  resetEstimateDraftData,
  updateIsBackFromCreateTaxInEstimatesSlice,
  initializeEstimateForEdit,
  updateIsInitialisingEstimateFromAPI,
  updateAssignees,
  initializeEstimateFromJob,
  initializeEstimateFromInvoice,
  setEstimateFromOtherEntities,
  setEntityType,
  updateInvoiceFeeSettingsInEstimates,
  updateEstimateReferenceNumber,
  updateLeadSource,
  updateLeadSourceType,
  updateEstimatesTnc,
  resetEstimateAttachmentState
} = estimatesSlice.actions;

// Selectors
export const selectEstimate = (state: RootState) => state.rootReducer.estimates;
export const selectCurrentEstimate = (state: RootState) => state.rootReducer.estimates.currentEstimate;
export const selectEstimateCustomer = (state: RootState) => state.rootReducer.estimates.estimateCustomer;
export const selectServiceAddress = (state: RootState) => state.rootReducer.estimates.serviceAddress;
export const selectBillingAddress = (state: RootState) => state.rootReducer.estimates.billingAddress;
export const selectEstimateTitle = (state: RootState) => state.rootReducer.estimates.estimateTitle;
export const selectEstimateDescription = (state: RootState) => state.rootReducer.estimates.estimateDescription;
export const selectEstimateLoading = (state: RootState) => state.rootReducer.estimates.loading;
export const selectEstimateError = (state: RootState) => state.rootReducer.estimates.error;
export const selectEstimateValidationErrors = (state: RootState) => state.rootReducer.estimates.validationErrors;
export const selectExpiryDate = (state: RootState) => new Date(state.rootReducer.estimates.expiryDate);
export const selectEstimateNotes = (state: RootState) => state.rootReducer.estimates.notes;
export const selectEstimateAttachments = (state: RootState) => state.rootReducer.estimates.attachments;
export const selectIsSignatureEnabled = (state: RootState) => state.rootReducer.estimates.isSignatureEnabled;
export const selectEstimateLineItems = (state: RootState) => state.rootReducer.estimates.estimateLineItems;
export const selectEstimateLineItemsTotal = (state: RootState) => state.rootReducer.estimates.estimateLineItemsTotal;
export const selectEstimateSubtotal = (state: RootState) => state.rootReducer.estimates.estimateSubTotal;
export const selectEstimateFinalAmount = (state: RootState) => state.rootReducer.estimates.estimateFinalAmount;
export const selectEstimateDiscountFormData = (state: RootState) =>
  state.rootReducer.estimates.estimateDiscountFormData;
export const selectEstimateSelectedServiceCharge = (state: RootState) =>
  state.rootReducer.estimates.estimateSelectedServiceCharge;
export const selectCurrentSurchargeInEstimates = (state: RootState) =>
  state.rootReducer.estimates.estimateCurrentSurcharge;
export const selectAppliedEstimateLineItemLevelTaxes = (state: RootState) =>
  state.rootReducer.estimates.estimateAppliedLineItemLevelTaxes;
export const selectIsServiceFeeEnabledFromEstimate = (state: RootState) =>
  state.rootReducer.estimates.estimateIsServiceFeeEnabled;
export const selectGlobalLineItemTaxesEstimatesSlice = (state: RootState) =>
  state.rootReducer.estimates.estimateGlobalLineItemTaxes;
export const selectGlobalServiceChargeTaxesInEstimateSlice = (state: RootState) =>
  state.rootReducer.estimates.estimateGlobalServiceChargeTaxes;
export const selectIsBackFromCreateTaxFromEstimatesSlice = (state: RootState) =>
  state.rootReducer.estimates.estimateIsBackFromCreateTaxRoute;
export const selectEstimateLineItemDraftModalData = (state: RootState) =>
  state.rootReducer.estimates.estimateDraftModalData;

export const selectEstimateDiscountSettings = (state: RootState) => state.rootReducer.estimates.discountSettings;

export const selectIsInitialisingEstimateFromAPI = (state: RootState) =>
  state.rootReducer.estimates.isInitialisingEstimateFromAPI;

export const selectAssignees = (state: RootState) => state.rootReducer.estimates.assignees;

export const selectEstimateReferenceNumber = (state: RootState) => state.rootReducer.estimates.estimateReferenceNumber;
export const selectInitialEstimateReferenceNumber = (state: RootState) =>
  state.rootReducer.estimates.initialEstimateReferenceNumber;

export const selectLeadSource = (state: RootState) => state.rootReducer.estimates.leadSource;
export const selectLeadSourceType = (state: RootState) => state.rootReducer.estimates.leadSourceType;

export const selectEstimatesTnc = (state: RootState) => state.rootReducer.estimates.estimatesTnc;
export const selectEstimateTncEdited = (state: RootState) => state.rootReducer.estimates.estimateTncEdited;

export const selectCascadingRelatedEntityData = (state: RootState) => ({
  isFromInvoice: state.rootReducer.estimates.isFromInvoice,
  isFromJob: state.rootReducer.estimates.isFromJob,
  relatedInvoiceStatus: state.rootReducer.estimates.relatedInvoiceStatus,
  invoiceNumber: state.rootReducer.estimates.invoiceNumber,
  jobNumber: state.rootReducer.estimates.jobNumber,
  jobId: state.rootReducer.estimates.jobId,
  invoiceId: state.rootReducer.estimates.invoiceId
});

export default estimatesSlice.reducer;
